import React, { useState, useEffect, useMemo } from 'react';
import {
  Loader2,
  AlertTriangle,
  ArrowLeft,
  Receipt as ReceiptIcon,
  Printer,
  Pencil,
  Smartphone,
  Banknote,
  Calendar,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import PeriodReceiptDialog from './PeriodReceiptDialog';
import MemberReceiptDialog from './MemberReceiptDialog';
import DistributeMpesaDialog from './DistributeMpesaDialog';
import receiptsApi from './services/receiptsApi';

// ---------------------------------------------------------------------
// "View Receipts" drill-down for one period: header (group + period info
// + total amount sent + group summary receipt), a Mpesa/Cash tab switch,
// and the member receipts table. Member receipts and the header stat
// boxes now come from the real API (period_member_receipts / period_stats
// in mpesa/views/period_receipts_views.py).
//
// Still stubs, per instruction — not wired up yet:
//   - Group Summary Receipt / Final Period Receipt dialogs themselves
//     already render real numbers (see PeriodReceiptDialog.js); only
//     their "Print Receipt" buttons remain unwired.
//   - All three "Print All ..." buttons.
//   - The per-row Edit button (distribute-across-periods flow).
//   Print actions are where the receipt-printer integration eventually
//   hooks in.
// ---------------------------------------------------------------------

const formatKSh = (amount) => `KSh ${Math.round(amount || 0).toLocaleString()}`;

const formatShortDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
  });
};

const getStatusBadgeColor = (status, isDark) => {
  switch (status) {
    case 'open': return isDark ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800';
    case 'active': return isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800';
    case 'closed': return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    default: return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
  }
};

const ReceiptDetail = ({ period, isDark, onBack }) => {
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('mpesa'); // 'mpesa' | 'cash' — mpesa is the default

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Which dialog is open, if any.
  const [receiptDialogVariant, setReceiptDialogVariant] = useState(null); // null | 'summary' | 'final'
  const [memberDialogReceipt, setMemberDialogReceipt] = useState(null);
  const [distributeDialogReceipt, setDistributeDialogReceipt] = useState(null);

  useEffect(() => {
    if (!period) return;
    setActiveTab('mpesa');
    loadReceiptsForPeriod(period);
    loadStatsForPeriod(period);
    // eslint-disable-next-line
  }, [period?.id]);

  const loadReceiptsForPeriod = async (p) => {
    setReceiptsLoading(true);
    try {
      const data = await receiptsApi.fetchPeriodMemberReceipts(p.group.id, p.meetingDate, p.type);
      setReceipts(data);
    } catch (err) {
      console.error('Error loading receipts:', err);
      setReceipts([]);
    } finally {
      setReceiptsLoading(false);
    }
  };

  const loadStatsForPeriod = async (p) => {
    setStatsLoading(true);
    try {
      const data = await receiptsApi.fetchPeriodStats(p.group.id, p.meetingDate, p.type);
      setStats(data);
    } catch (err) {
      console.error('Error loading period stats:', err);
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleGroupSummaryReceipt = () => {
    setReceiptDialogVariant('summary');
  };

  const handleFinalPeriodReceipt = () => {
    setReceiptDialogVariant('final');
  };

  // Bulk-print buttons are still stubs — printer integration comes later.
  const handlePrintAllCustomerCopy = () => {
    alert(`Print All ${activeTab === 'mpesa' ? 'Mpesa' : 'Cash'} Receipts (Customer Copy) is not wired up yet.`);
  };

  const handlePrintAll = () => {
    alert(`Print All ${activeTab === 'mpesa' ? 'Mpesa' : 'Cash'} Receipts is not wired up yet.`);
  };

  const handlePrintAllOfficeCopy = () => {
    alert(`Print All ${activeTab === 'mpesa' ? 'Mpesa' : 'Cash'} Receipts (Office Copy) is not wired up yet.`);
  };

  const handlePrintReceipt = (receipt) => {
    setMemberDialogReceipt(receipt);
  };

  // Only meaningful for Mpesa — the row's Edit button is disabled for
  // Cash, so this should only ever be called with an mpesa receipt.
  const handleEditReceipt = (receipt) => {
    if (receipt.type !== 'mpesa') return;
    setDistributeDialogReceipt(receipt);
  };

  const tabReceipts = useMemo(() => receipts.filter(r => r.type === activeTab), [receipts, activeTab]);
  const mpesaCount = useMemo(() => receipts.filter(r => r.type === 'mpesa').length, [receipts]);
  const cashCount = useMemo(() => receipts.filter(r => r.type === 'cash').length, [receipts]);
  // Cash actually collected + online payments — raw money in, not the
  // post-deductions net figure (that's the separate "Net Cash Remitted"
  // stat box below).
  const totalAmountSent = stats ? (stats.cashAmount || 0) + (stats.onlinePayments || 0) : 0;

  // Final Period Receipt only makes sense once the meeting has actually
  // happened — enabled on the meeting date itself or any day after.
  const isMeetingDateReached = useMemo(() => {
    if (!period?.meetingDate) return false;
    const meeting = new Date(period.meetingDate);
    meeting.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return meeting <= today;
  }, [period]);

  // The small stat-box row under the header, similar in structure to the
  // reference screenshot's meta boxes (bold value, small caps label below)
  // — count + the account-type and payment-method breakdown of the total.
  const statBoxes = useMemo(() => {
    if (!stats) return [];
    return [
      { label: 'Receipts', value: (stats.receiptsCount || 0).toLocaleString() },
      { label: 'Regular Account', value: formatKSh(stats.regularAccountAmount) },
      { label: 'SF Account', value: formatKSh(stats.sfAccountAmount) },
      { label: 'Mpesa Amount', value: formatKSh(stats.onlinePayments) },
      { label: 'Cash Amount', value: formatKSh(stats.cashAmount) },
      { label: 'Net Cash Remitted', value: formatKSh(stats.netCashRemitted) },
    ];
  }, [stats]);

  if (!period || receiptsLoading || statsLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className={`ml-3 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading receipts...</span>
      </div>
    );
  }

  return (
    <div className="pb-32">
      {/* Header panel: circular back button, then group name, then status;
          meeting date as a highlighted pill; total amount sent presented
          as a plain prominent figure — deliberately not a card or a table
          row; a row of small stat boxes underneath breaks the total down
          by account type and payment method. */}
      <div className={`rounded-lg shadow-sm border p-5 mb-6 ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <button
                onClick={onBack}
                title="Back to Periods"
                className="w-9 h-9 shrink-0 rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center justify-center transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {period.group.name}
              </h2>
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadgeColor(period.status, isDark)}`}>
                {period.status}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-2 ml-12">
              <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                isDark ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'bg-amber-50 text-amber-700 border-amber-300'
              }`}>
                <Calendar className="w-3.5 h-3.5" />
                {formatShortDate(period.meetingDate)}
              </span>
              <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                {period.periodName}
              </span>
            </div>
          </div>

          <div className="text-right">
            <p className={`text-xs font-medium uppercase tracking-wide ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Total Amount Sent
            </p>
            <p className={`text-4xl font-bold mt-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {formatKSh(totalAmountSent)}
            </p>
          </div>
        </div>

        {/* Stat boxes: count + the account-type / payment-method breakdown */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-5">
          {statBoxes.map((box) => (
            <div
              key={box.label}
              className={`rounded-lg border px-3 py-2.5 ${isDark ? 'bg-gray-800/60 border-gray-700' : 'bg-gray-50 border-gray-200'}`}
            >
              <p className={`text-base font-bold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {box.value}
              </p>
              <p className={`text-[11px] uppercase tracking-wide mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {box.label}
              </p>
            </div>
          ))}
        </div>

        <div className={`mt-4 pt-4 border-t flex justify-end gap-2 ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            onClick={handleGroupSummaryReceipt}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            <ReceiptIcon className="w-4 h-4" />
            Group Summary Receipt
          </button>
          <button
            onClick={handleFinalPeriodReceipt}
            disabled={!isMeetingDateReached}
            title={isMeetingDateReached ? undefined : 'Available on or after the meeting date'}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              isMeetingDateReached
                ? 'text-white bg-blue-600 hover:bg-blue-700'
                : isDark
                  ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <ReceiptIcon className="w-4 h-4" />
            Final Period Receipt
          </button>
        </div>
      </div>

      {/* Cash / Mpesa tabs + the table of member receipts */}
      <div className={`rounded-lg shadow-sm border overflow-hidden ${isDark ? 'bg-[#2b323c] border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`inline-flex rounded-lg p-1 ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
            <button
              onClick={() => setActiveTab('mpesa')}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'mpesa'
                  ? isDark ? 'bg-[#2b323c] text-white shadow' : 'bg-white text-gray-900 shadow'
                  : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Smartphone className="w-4 h-4" />
              Mpesa
              <span className={`ml-0.5 text-xs ${activeTab === 'mpesa' ? (isDark ? 'text-gray-400' : 'text-gray-500') : (isDark ? 'text-gray-500' : 'text-gray-400')}`}>
                ({mpesaCount})
              </span>
            </button>
            <button
              onClick={() => setActiveTab('cash')}
              className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                activeTab === 'cash'
                  ? isDark ? 'bg-[#2b323c] text-white shadow' : 'bg-white text-gray-900 shadow'
                  : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Banknote className="w-4 h-4" />
              Cash
              <span className={`ml-0.5 text-xs ${activeTab === 'cash' ? (isDark ? 'text-gray-400' : 'text-gray-500') : (isDark ? 'text-gray-500' : 'text-gray-400')}`}>
                ({cashCount})
              </span>
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handlePrintAllCustomerCopy}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
              }`}
            >
              <Printer className="w-4 h-4" />
              Print All Customer Copy
            </button>
            <button
              onClick={handlePrintAll}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
              }`}
            >
              <Printer className="w-4 h-4" />
              {activeTab === 'mpesa' ? 'Print All Mpesa Receipts' : 'Print All Cash Receipts'}
            </button>
            <button
              onClick={handlePrintAllOfficeCopy}
              className={`inline-flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? 'bg-[#2b323c] border-gray-700 text-gray-200 hover:border-blue-500'
                  : 'bg-white border-gray-300 text-gray-900 hover:border-blue-500'
              }`}
            >
              <Printer className="w-4 h-4" />
              Print All Office Copy
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className={`border-b ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'}`}>
              <tr>
                {/* #N (matches the Group Summary / Final Period receipt row and the
                   customer copy badge) gets its own leftmost column, deliberately
                   unlabeled so it reads as an index, not a data column. */}
                <th className="px-6 py-3 w-10"></th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Transactions</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Member Name</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Member Phone</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Amount</th>
                <th className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Type</th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
                <th className={`px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Is Printed?</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'}`}>
              {tabReceipts.map((receipt) => (
                <tr key={receipt.id} className={isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}>
                  <td className={`pl-6 pr-2 py-4 text-left text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    #{receipts.findIndex(r => r.id === receipt.id) + 1}
                  </td>
                  <td className={`px-6 py-4 text-center font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {receipt.transactionsCount}
                  </td>
                  <td className={`px-6 py-4 font-medium ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                    {receipt.memberName}
                  </td>
                  <td className={`px-6 py-4 text-sm ${isDark ? 'text-gray-300' : 'text-gray-900'}`}>
                    {receipt.memberPhone}
                  </td>
                  <td className={`px-6 py-4 font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                    {formatKSh(receipt.amount)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold uppercase ${
                      receipt.type === 'mpesa'
                        ? isDark ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'
                        : isDark ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {receipt.type === 'mpesa' ? <Smartphone className="w-3.5 h-3.5" /> : <Banknote className="w-3.5 h-3.5" />}
                      {receipt.type === 'mpesa' ? 'Mpesa' : 'Cash'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => handlePrintReceipt(receipt)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Printer className="w-3.5 h-3.5" />
                        Print Receipt
                      </button>
                      <button
                        onClick={() => handleEditReceipt(receipt)}
                        disabled={receipt.type !== 'mpesa'}
                        title={receipt.type === 'mpesa' ? 'Distribute this Mpesa payment across periods' : 'Only Mpesa receipts can be redistributed'}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 border text-sm font-medium rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                          isDark ? 'border-gray-600 text-gray-200 hover:border-gray-400' : 'border-gray-300 text-gray-700 hover:border-gray-500'
                        }`}
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-center" title={receipt.isPrinted ? 'Printed' : 'Not printed'}>
                      {receipt.isPrinted ? (
                        <CheckCircle2 className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                      ) : (
                        <XCircle className={`w-5 h-5 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {tabReceipts.length === 0 && (
          <div className={`text-center py-12 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            <AlertTriangle className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
            <p className="text-lg">No {activeTab === 'mpesa' ? 'Mpesa' : 'Cash'} receipts for this period.</p>
          </div>
        )}

        <div className={`px-5 py-3 border-t text-sm ${isDark ? 'border-gray-700 text-gray-400' : 'border-gray-200 text-gray-600'}`}>
          Showing {tabReceipts.length} of {receipts.length} member receipts
        </div>
      </div>

      <PeriodReceiptDialog
        open={receiptDialogVariant !== null}
        onClose={() => setReceiptDialogVariant(null)}
        period={period}
        receipts={receipts}
        variant={receiptDialogVariant || 'summary'}
      />

      <MemberReceiptDialog
        open={memberDialogReceipt !== null}
        onClose={() => setMemberDialogReceipt(null)}
        period={period}
        receipt={memberDialogReceipt}
        receipts={receipts}
      />

      <DistributeMpesaDialog
        open={distributeDialogReceipt !== null}
        onClose={() => setDistributeDialogReceipt(null)}
        period={period}
        receipt={distributeDialogReceipt}
      />
    </div>
  );
};

export default ReceiptDetail;